/**
 * Bounded fixed-step clock for deterministic simulation on variable-refresh
 * displays. Excess hitch time is intentionally dropped instead of producing a
 * spiral of death that makes the next frame even later.
 */
export class FixedStepClock {
  private accumulator = 0;

  constructor(
    readonly stepSeconds = 1 / 60,
    readonly maxSteps = 3,
  ) {
    if (!(stepSeconds > 0) || !Number.isInteger(maxSteps) || maxSteps < 1) {
      throw new Error('FixedStepClock requires a positive step and integer maxSteps');
    }
  }

  advance(frameSeconds: number, step: (seconds: number) => void): number {
    // Contract v1: consume at most 50 ms from a rendered frame. Keeping this
    // separate from maxSteps makes the order of operations unambiguous.
    const validRealDelta = Number.isFinite(frameSeconds) ? Math.max(0, frameSeconds) : 0;
    const bounded = Math.min(validRealDelta, 0.05);
    this.accumulator += bounded;
    let count = 0;
    while (this.accumulator + Number.EPSILON >= this.stepSeconds && count < this.maxSteps) {
      step(this.stepSeconds);
      this.accumulator -= this.stepSeconds;
      count++;
    }
    // Do not preserve old time after the catch-up ceiling. A 180 ms texture
    // upload therefore produces at most three physics steps, never a spiral.
    if (count === this.maxSteps && this.accumulator + Number.EPSILON >= this.stepSeconds) {
      this.accumulator = 0;
    }
    return count;
  }

  reset(): void {
    this.accumulator = 0;
  }
}
